// src/pages/tutorials/TutorialImportingData.jsx
import React, { Component, Fragment } from "react";
import { Helmet } from 'react-helmet';
import HeaderOne from '../../components/header/HeaderOne'; // Adjusted path
import Footer from '../../components/footer/Footer'; // Adjusted path
import ScrollToTop from 'react-scroll-up';
import { FiChevronUp } from "react-icons/fi";
import Breadcrumb from "../../components/common/Breadcrumb"; // Adjusted path

class TutorialImportingData extends Component {
    render() {
        return (
            <Fragment>
                <Helmet>
                    <title>Importing Datasets | Eidocell Documentation</title>
                    <meta name="description" content="Learn how to create new analysis sessions and import your microscopy images into Eidocell for processing and analysis." />
                </Helmet>

                <HeaderOne />

                <Breadcrumb title={'Importing Your First Dataset'} />

                <div className="main-content-area ptb--120 bg_color--1">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-10 offset-lg-1">
                                <div className="startup-guide-content"> {/* Reusing startup-guide styles */}
                                    <p className="lead">
                                        The first step in any image analysis workflow is to bring your data into the application.
                                        In Eidocell, this involves creating an "Analysis Session" which acts as a dedicated workspace for a specific dataset or experiment.
                                        This tutorial will cover creating a new session and importing your images.
                                    </p>

                                    <section id="creating-session" className="mb--40">
                                        <h2>1. Creating a New Session</h2>
                                        <p>
                                            To start working with your images, you first need to create a new session:
                                        </p>
                                        <ol>
                                            <li>
                                                Launch Eidocell.
                                            </li>
                                            <li>
                                                In the main navigation bar on the left, click the "Sessions" icon (usually a folder icon).
                                                <p><code>[SCREENSHOT: Main navigation bar with "Sessions" icon highlighted]</code></p>
                                            </li>
                                            <li>
                                                This will open the "Select Session" dialog. This dialog lists any existing sessions you've previously created. To create a new one, click the "Add Folder" card (often depicted with a '+' icon or similar).
                                                <p><code>[SCREENSHOT: "Select Session" dialog showing the "Add Folder" card]</code></p>
                                            </li>
                                            <li>
                                                A "Create New Session" dialog will appear. You need to provide two pieces of information:
                                                <ul>
                                                    <li><strong>Session Name:</strong> A descriptive name for your analysis project (e.g., "Experiment_Alpha_Dataset1", "Drug_Treatment_Analysis").</li>
                                                    <li><strong>Images Folder:</strong> Click the "Choose images folder" button to browse your computer and select the folder that contains the microscopy images you want to analyze. Eidocell will scan this folder for supported image formats (e.g., TIFF, PNG, JPG).</li>
                                                </ul>
                                                <p><code>[SCREENSHOT: "Create New Session" dialog with fields for Session Name and Images Folder selection]</code></p>
                                            </li>
                                            <li>
                                                Once you've entered a name and selected your image folder, click the "Create" button.
                                            </li>
                                            <li>
                                                Eidocell will create a new session folder on your computer (usually in a "sessions" directory within the Eidocell application data folder). This session folder will store all metadata, features, masks, and analysis results associated with this dataset.
                                            </li>
                                            <li>
                                                After successful creation, the "Select Session" dialog will update to show your new session card. Click on this card to open and load the session.
                                                <p><code>[SCREENSHOT: "Select Session" dialog now showing the newly created session card, with an arrow pointing to click it]</code></p>
                                            </li>
                                        </ol>
                                        <p>
                                            Upon loading the session, the workspace area will become active, and Eidocell will begin importing and creating thumbnails for the images in the selected folder. This might take a few moments depending on the number and size of your images.
                                        </p>
                                    </section>

                                    <section id="gallery-view" className="mb--40">
                                        <h2>2. Navigating the Gallery View</h2>
                                        <p>
                                            Once the images are imported, they will be displayed in the "Gallery" view, which is typically the default view when a session is loaded.
                                        </p>
                                        <p><code>[SCREENSHOT: Eidocell Gallery View populated with image thumbnails]</code></p>
                                        <p>In the Gallery View, you can:</p>
                                        <ul>
                                            <li><strong>Browse Images:</strong> Scroll through your dataset. Thumbnails provide a quick visual overview.</li>
                                            <li><strong>Image Scale:</strong> Use the "Scale" slider in the control panel at the top of the Gallery view to adjust the size of the thumbnails for comfortable viewing.</li>
                                            <li><strong>Select Images:</strong> Click on images to select them. Standard selection methods apply:
                                                <ul>
                                                    <li><strong>Single Click:</strong> Selects an image.</li>
                                                    <li><strong>Ctrl/Cmd + Click:</strong> Add or remove an image from the current selection.</li>
                                                    <li><strong>Shift + Click:</strong> Select a range of images (from the last clicked image to the current one).</li>
                                                </ul>
                                            </li>
                                            <li><strong>Context Menu:</strong> Right-clicking on an image (or a selection of images) will open a context menu with available actions, such as assigning them to a class.</li>
                                        </ul>
                                        <p><code>[SCREENSHOT: Gallery view with an image right-clicked, showing the context menu with "Assign Class" option]</code></p>
                                    </section>

                                    <section id="image-formats" className="mb--40">
                                        <h2>3. Supported Image Formats & Considerations</h2>
                                        <p>Eidocell aims to support common image formats used in microscopy, including:</p>
                                        <ul>
                                            <li>TIFF (<code>.tif</code>, <code>.tiff</code>) - Both single and multi-page (stacks, time-series) if applicable, though primary processing is often per-frame.</li>
                                            <li>PNG (<code>.png</code>)</li>
                                            <li>JPEG (<code>.jpg</code>, <code>.jpeg</code>)</li>
                                            <li>BMP (<code>.bmp</code>)</li>
                                        </ul>
                                        <p>
                                            <strong>Best Practices for Import:</strong>
                                        </p>
                                        <ul>
                                            <li><strong>Consistent Naming:</strong> While not strictly required, having a consistent naming convention for your image files can make management easier.</li>
                                            <li><strong>Folder Structure:</strong> Place all images for a single experiment or dataset into one primary folder for import. Subfolders are generally not scanned recursively during the initial import for a session, so ensure all relevant images are in the top-level folder you select.</li>
                                            <li><strong>Image Integrity:</strong> Ensure your image files are not corrupted and are in a standard format.</li>
                                        </ul>
                                    </section>

                                    <section id="next-steps">
                                        <h2>Next Steps</h2>
                                        <p>
                                            With your images successfully imported into an Eidocell session, you're ready to move on to the core analysis tasks.
                                            The next logical step is typically segmenting your cells to identify their boundaries.
                                        </p>
                                        <p>
                                            Proceed to the next tutorial: <a href="#/usage/tutorials/basic-segmentation">Basic Image Segmentation</a>.
                                        </p>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />

                <div className="backto-top">
                    <ScrollToTop showUnder={160}>
                        <FiChevronUp />
                    </ScrollToTop>
                </div>
            </Fragment>
        )
    }
}

export default TutorialImportingData;